import { createFileRoute, Outlet, useMatches } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import {
  Badge,
  Card,
  Input,
  Spinner,
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
  Td,
} from '@/components/admin/ui';
import {
  EmptyState,
  Pagination,
  SortTh,
  UserLink,
} from '@/components';
import type { AdminSortId } from '@/components/SortTh';
import { formatAdminDateTime, formatInt } from '@/lib/format';
import { apiJson } from '@/lib/api';

type UserItem = {
  id: string;
  created_at: string;
  updated_at: string;
  display_name: string | null;
  email: string | null;
  clerk_id: string | null;
  deleted_at: string | null;
  device_count: number;
};

type ListRes = { items: UserItem[]; total: number; page: number; page_size: number };

type SortKey =
  | 'created_at_desc'
  | 'created_at_asc'
  | 'updated_at_desc'
  | 'updated_at_asc';

function sortToParts(s: SortKey): { col: AdminSortId; direction: 'asc' | 'desc' } {
  if (s.startsWith('updated')) {
    return { col: 'updated', direction: s.endsWith('_asc') ? 'asc' : 'desc' };
  }
  return { col: 'created', direction: s.endsWith('_asc') ? 'asc' : 'desc' };
}

function partsToSort(col: AdminSortId, direction: 'asc' | 'desc'): SortKey {
  const b = col === 'updated' ? 'updated_at' : 'created_at';
  return `${b}_${direction === 'asc' ? 'asc' : 'desc'}` as SortKey;
}

function UsersList() {
  const [q, setQ] = useState('');
  const [qDebounced, setQDebounced] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 50;
  const [sort, setSort] = useState<SortKey>('created_at_desc');

  useEffect(() => {
    const t = setTimeout(() => {
      setQDebounced(q);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['admin', 'users', qDebounced, status, page, pageSize, sort],
    queryFn: () => {
      const params = new URLSearchParams();
      if (qDebounced.trim()) params.set('q', qDebounced.trim());
      if (status === 'active') params.set('status', 'active');
      if (status === 'deleted') params.set('status', 'deleted');
      params.set('page', String(page));
      params.set('page_size', String(pageSize));
      params.set('sort', sort);
      return apiJson<ListRes>(`/v1/admin/users?${params.toString()}`);
    },
  });

  const { col: activeCol, direction } = sortToParts(sort);

  const onHeaderSort = (clicked: AdminSortId) => {
    if (activeCol === clicked) {
      setSort(partsToSort(clicked, direction === 'desc' ? 'asc' : 'desc'));
    } else {
      setSort(partsToSort(clicked, 'desc'));
    }
    setPage(1);
  };

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold tracking-tight">Users</h1>
      <Card>
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="min-w-0 flex-1">
            <Input
              label="Search (name, email, clerk id, or user id)"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="type to filter"
            />
          </div>
          <div className="w-full sm:w-40">
            <label className="mb-2 block text-sm text-nova-muted-foreground">Status</label>
            <select
              className="w-full rounded-lg border border-nova-border h-8 px-2.5 text-sm"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All</option>
              <option value="active">Active</option>
              <option value="deleted">Deleted</option>
            </select>
          </div>
        </div>

        {isLoading && (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        )}
        {isError && (
          <p className="text-sm text-red-600">
            {(error as Error).message}
            <button
              type="button"
              className="ml-2 underline"
              onClick={() => {
                void refetch();
              }}
            >
              Retry
            </button>
          </p>
        )}
        {data && data.items.length === 0 && <EmptyState title="No users match" />}
        {data && data.items.length > 0 && (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Display name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Devices</TableHead>
                  <SortTh
                    sortId="created"
                    label="Created"
                    active={activeCol}
                    direction={direction}
                    onClick={onHeaderSort}
                  />
                  <SortTh
                    sortId="updated"
                    label="Updated"
                    active={activeCol}
                    direction={direction}
                    onClick={onHeaderSort}
                  />
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((u) => (
                  <TableRow key={u.id}>
                    <Td>
                      <UserLink userId={u.id} displayName={u.display_name}>
                        {u.display_name ?? '(no name)'}
                      </UserLink>
                    </Td>
                    <Td className="max-w-[200px] truncate text-nova-muted-foreground">
                      {u.email ?? '—'}
                    </Td>
                    <Td>
                      {u.deleted_at ? (
                        <Badge variant="danger">Deleted</Badge>
                      ) : (
                        <Badge variant="primary">Active</Badge>
                      )}
                    </Td>
                    <Td>{formatInt(u.device_count)}</Td>
                    <Td className="whitespace-nowrap text-nova-muted-foreground">
                      {formatAdminDateTime(u.created_at)}
                    </Td>
                    <Td className="whitespace-nowrap text-nova-muted-foreground">
                      {formatAdminDateTime(u.updated_at)}
                    </Td>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Pagination
              page={data.page}
              pageSize={data.page_size}
              total={data.total}
              onChange={setPage}
            />
          </>
        )}
      </Card>
    </div>
  );
}

function UsersLayout() {
  const matches = useMatches();
  const leafId = matches.at(-1)?.routeId;
  if (leafId === '/admin/users/$userId') {
    return <Outlet />;
  }
  return <UsersList />;
}

export const Route = createFileRoute('/admin/users')({ component: UsersLayout });
